<template>
  <b-row class="justify-content-md-center m-0">
    <b-col lg="6" class="mb-4">
      <b-spinner v-if="isFetching" />

      <b-row v-else>
        <b-col v-if="learningUnit">
          <base-bread-crumb :items="breadcrumbs" />

          <div v-if="learningUnit" class="h5 display-4 font-weight-bold mb-3 header__lu-title">
            {{ learningUnit.name }}
          </div>

          <!-- Button "Back To previous page" -->
          <div class="mb-3">
            <b-button
              variant="outline-primary"
              class="mb-4 btn__previous-page"
              @click="gotoPreviousPage()"
            >
              <span class="btn-wrapper--icon">
                <font-awesome-icon icon="arrow-left" />
              </span>
              <span class="btn-wrapper--label"> Back to previous page</span>
            </b-button>
          </div>

          <div class="d-flex justify-content-start">
            <base-card-post-schedule
              v-if="post && post.meeting"
              :post="post"
              :post-schedule="postSchedule"
              :date="new Date(post.meeting.start_time)"
              :is-private="!post.is_published"
              :show-date="true"
              :section-id="sectionId"
              :section="section"
              :is-show-video-player="false"
              :is-show-view-recording="false"
              :is-show-attachment="false"
              :is-owner="false"
              :check-consent="true"
            />
          </div>
        </b-col>
      </b-row>
    </b-col>

    <b-col v-if="consent && consent.consent === true" ref="page" lg="8">
      <b-spinner v-if="isFetching" />

      <b-row v-else>
        <b-col md="8">
          <base-video-player
            v-if="post.vimeo"
            :key="`video-${videoWidth}`"
            :video="post.vimeo"
            class="video__recording mb-4"
            :width="videoWidth"
            :height="videoHeight"
          />
        </b-col>
        <b-col md="4">
          <base-chat-message v-if="chatMessageFile" :link="chatMessageFile.url" />

          <b-card v-else class="font-weight-bold">
            <b-card-text class="text-center"> No chat messages available </b-card-text>
          </b-card>
        </b-col>
      </b-row>
    </b-col>
  </b-row>
</template>

<script>
import BaseBreadCrumb from '../../components/base-components/BaseBreadCrumb';
import { LearningUnit, Post, Section, Consent, User } from '../../models';
import PostSchedule from '../../models/PostSchedule';
import BaseCardPostSchedule from '../../components/base-components/BaseCardPostSchedule';
import BaseVideoPlayer from '../../components/base-components/BaseVideoPlayer';
import BaseChatMessage from '../../components/base-components/BaseChatMessage';
import { pageWidthChecker } from '@/mixins';

export default {
  name: 'PageLearnerVideoConference',

  components: {
    BaseBreadCrumb,
    BaseCardPostSchedule,
    BaseVideoPlayer,
    BaseChatMessage
  },
  mixins: [pageWidthChecker],

  data() {
    return {
      pageWidth: null,
      isFetching: false
    };
  },

  computed: {
    user() {
      return User.query().first();
    },

    learningUnitId() {
      return parseInt(this.$route.params.learningUnitId);
    },

    learningUnit() {
      return LearningUnit.find(this.learningUnitId);
    },

    postId() {
      return parseInt(this.$route.params.postId);
    },

    post() {
      return Post.query().with('meeting').with('vimeo').with('attachments').find(this.postId);
    },

    chatMessageFile() {
      return this.post && this.post.attachments ? this.post.attachments[0] : null;
    },

    sectionId() {
      return parseInt(this.$route.params.sectionId);
    },

    section() {
      return Section.find(this.sectionId);
    },

    postSchedule() {
      return PostSchedule.query()
        .where('post_id', this.postId)
        .where('section_id', this.sectionId)
        .first();
    },

    consent() {
      return this.post && this.user && this.sectionId
        ? Consent.query()
            .where('post_id', this.post.id)
            .where('user_id', this.user.id)
            .where('section_id', this.sectionId)
            .first()
        : null;
    },

    videoWidth() {
      return this.pageWidth ? this.pageWidth * 0.65 : this.windowWidth <= 650 ? 500 : 646;
    },

    videoHeight() {
      return this.windowWidth <= 650 ? 250 : 343;
    },

    breadcrumbs() {
      const { learningUnitId, sectionId } = this;

      return [
        {
          key: 'design-and-planning',
          name: 'Design and planning',
          to: {
            name: 'design-planning'
          }
        },
        {
          key: 'learning-unit-info',
          name: this.learningUnit?.name || '',
          to: {
            name: 'design-planning/info',
            params: {
              learningUnitId
            }
          }
        },
        {
          key: 'learning-feed',
          name: 'Learning',
          to: {
            name: 'learning-unit/feed',
            params: {
              learningUnitId,
              sectionId
            },
            query: {
              tab: 'posts'
            }
          }
        }
      ];
    }
  },

  watch: {
    '$route.path': {
      immediate: true,
      handler: 'onRouteChanged'
    }
  },

  methods: {
    onRouteChanged() {
      this.fetch();
    },

    async fetch() {
      this.isFetching = true;

      try {
        await LearningUnit.api().fetchByLearningUnitId(this.learningUnitId);

        await PostSchedule.api().findAll({
          post: this.postId,
          section: this.sectionId
        });

        await Post.api().fetchById(this.postId);
      } catch (error) {
        console.error(error);
        this.$toast.open({
          message: `Fail to load video conference, please try again.`,
          type: 'error',
          duration: 5000
        });
      } finally {
        this.isFetching = false;
      }
    },

    gotoPreviousPage() {
      if (this.$route.query.previousPath) {
        const { previousPath } = this.$route.query;

        this.$router.push({ path: previousPath });
      } else {
        this.$router.go(-1);
      }

      // this.$router.push({
      //   name: "learning-unit/feed",
      //   params: {
      //     learningUnitId: this.learningUnitId,
      //     sectionId: this.sectionId,
      //   },
      //   query: { ...this.$route.query },
      // });
    }
  }
};
</script>

<style lang="scss" scoped></style>
